import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';
import Particles from 'react-tsparticles';
import { loadFull } from 'tsparticles';

interface HeroSectionProps {
  taglines: string[];
  onCtaClick: () => void;
  darkMode: boolean;
  primaryCtaText?: string;
  secondaryCtaText?: string;
  onSecondaryCtaClick?: () => void;
  title?: string;
  subtitle?: string;
}

// Animated Taglines Component
const TaglineAnimation: React.FC<{ taglines: string[], darkMode: boolean }> = ({ taglines, darkMode }) => {
  const [visibleTaglines, setVisibleTaglines] = useState<{
    id: number, 
    text: string, 
    position: {x: number, y: number},
    velocity: {x: number, y: number},
    scale: number,
    rotation: number,
    randomOpacity?: number
  }[]>([]);
  
  const maxVisibleTaglines = 3; // Maximum number of taglines visible at once
  const usedTexts = useRef(new Set<string>());
  
  // Animation frame for continuous movement with more randomness
  useEffect(() => {
    if (visibleTaglines.length === 0) return;
    
    const animationFrame = requestAnimationFrame(() => {
      setVisibleTaglines(prev => prev.map(tagline => {
        // Add slight random variations to velocity for more natural movement
        const velocityVariationX = (Math.random() - 0.5) * 0.05; // Small random change
        const velocityVariationY = (Math.random() - 0.5) * 0.05; // Small random change
        
        // Update position based on velocity with slight randomness
        const newX = tagline.position.x + (tagline.velocity.x + velocityVariationX) * 0.01;
        const newY = tagline.position.y + (tagline.velocity.y + velocityVariationY) * 0.01;
        
        // Ensure taglines stay within bounds
        const boundedX = Math.max(5, Math.min(95, newX));
        const boundedY = Math.max(10, Math.min(90, newY));
        
        // If we hit a boundary, reverse direction with more randomness
        const newVelocityX = boundedX !== newX 
          ? -tagline.velocity.x * (0.7 + Math.random() * 0.6) // More random bounce (0.7-1.3)
          : tagline.velocity.x + velocityVariationX; // Add small drift
          
        const newVelocityY = boundedY !== newY 
          ? -tagline.velocity.y * (0.7 + Math.random() * 0.6) // More random bounce
          : tagline.velocity.y + velocityVariationY; // Add small drift
        
        // Occasionally add a small random impulse for more dynamic movement
        const impulseX = Math.random() < 0.02 ? (Math.random() - 0.5) * 0.5 : 0;
        const impulseY = Math.random() < 0.02 ? (Math.random() - 0.5) * 0.5 : 0;
        
        return {
          ...tagline,
          position: { x: boundedX, y: boundedY },
          velocity: { 
            x: newVelocityX + impulseX, 
            y: newVelocityY + impulseY 
          },
          // More varied rotation changes
          rotation: tagline.rotation + (Math.random() * 0.2 - 0.1), // More random rotation
          // Slightly vary scale over time for subtle "breathing" effect
          scale: tagline.scale * (1 + (Math.random() * 0.01 - 0.005))
        };
      }));
    });
    
    return () => cancelAnimationFrame(animationFrame);
  }, [visibleTaglines]);
  
  // Track the most recently used tagline to prevent back-to-back duplicates
  const lastUsedTagline = useRef<string | null>(null);
  
  // Add new taglines periodically
  useEffect(() => {
    if (!taglines.length) return;
    
    // Randomize the interval between taglines for more natural appearance
    const getRandomInterval = () => {
      // Base interval plus random variation (3.5-6.5 seconds)
      return 4000 * (0.7 + Math.random() * 0.6);
    };
    
    // Add a new tagline with randomized timing
    const interval = setInterval(() => {
      setVisibleTaglines(prev => {
        // Remove oldest tagline if we've reached the maximum
        const updated = prev.length >= maxVisibleTaglines 
          ? prev.slice(1) 
          : [...prev];
        
        // When removing a tagline, remove it from the used set
        if (prev.length >= maxVisibleTaglines && prev.length > 0) {
          usedTexts.current.delete(prev[0].text);
        }
        
        // Find taglines that aren't currently in use and aren't the last used tagline
        const availableTaglines = taglines.filter(text => 
          !usedTexts.current.has(text) && text !== lastUsedTagline.current
        );
        
        // If we don't have any available taglines that meet our criteria
        if (availableTaglines.length === 0) {
          // If all taglines are used, clear the set and start over
          if (usedTexts.current.size >= taglines.length - 1) {
            usedTexts.current.clear();
            // But still avoid the last used tagline
            const newAvailable = taglines.filter(text => text !== lastUsedTagline.current);
            availableTaglines.push(...newAvailable);
          } else {
            // Otherwise, just use any tagline that's not the last used one
            availableTaglines.push(...taglines.filter(text => text !== lastUsedTagline.current));
          }
        }
        
        // Select a random unused tagline
        const randomIndex = Math.floor(Math.random() * availableTaglines.length);
        const selectedText = availableTaglines[randomIndex];
        
        // Update the last used tagline reference
        lastUsedTagline.current = selectedText;
        
        // Mark this tagline as used
        usedTexts.current.add(selectedText);
        
        // Random position in the lower half of the hero section with more variation
        const randomX = 5 + Math.random() * 90; // 5% to 95% of container width
        const randomY = 55 + Math.random() * 35; // 55% to 90% of container height - strictly lower half
        
        // More varied randomized direction vectors with varying magnitudes
        const randomMagnitude = 0.3 + Math.random() * 2.0; // Random magnitude between 0.3 and 2.3
        const randomAngle = Math.random() * Math.PI * 2; // Random angle in radians (0 to 2π)
        
        // Convert angle and magnitude to x,y velocity components
        const randomVelocityX = Math.cos(randomAngle) * randomMagnitude;
        const randomVelocityY = Math.sin(randomAngle) * randomMagnitude;
        
        // More variation in scale and rotation
        const randomScale = 0.85 + Math.random() * 0.3; // 0.85 to 1.15
        const randomRotation = (Math.random() - 0.5) * 3; // More rotation (-1.5 to 1.5 degrees)
        
        // Random opacity variation
        const randomOpacity = 0.7 + Math.random() * 0.3; // 0.7 to 1.0
        
        return [
          ...updated, 
          {
            id: Date.now(),
            text: selectedText,
            position: { x: randomX, y: randomY },
            velocity: { x: randomVelocityX, y: randomVelocityY },
            scale: randomScale,
            rotation: randomRotation,
            randomOpacity: randomOpacity
          }
        ];
      });
    }, getRandomInterval()); // Randomized spawn rate
    
    return () => clearInterval(interval);
  }, [taglines]);
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <AnimatePresence>
        {visibleTaglines.map((tagline) => (
          <motion.div
            key={tagline.id}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: tagline.randomOpacity || 0.8, 
              scale: tagline.scale,
              x: `${tagline.position.x}%`,
              y: `${tagline.position.y}%`,
              rotate: tagline.rotation,
              transition: { 
                opacity: { duration: 1.5 + Math.random(), ease: "easeOut" }, // Randomized fade-in
                scale: { duration: 1.5 + Math.random(), ease: "easeOut" }, // Randomized scale animation
                x: { duration: 1 + Math.random(), ease: "linear" }, // Randomized position changes
                y: { duration: 1 + Math.random(), ease: "linear" }, // Randomized position changes
                rotate: { duration: 1.5 + Math.random(), ease: "linear" } // Randomized rotation
              }
            }}
            exit={{ 
              opacity: 0, 
              scale: 0.8,
              transition: { 
                duration: 1.5, // Longer fade-out
                ease: "easeIn" 
              }
            }}
            className={`absolute text-lg md:text-2xl font-light tracking-wider ${
              darkMode ? 'text-blue-300' : 'text-blue-600'
            }`}
            style={{ 
              position: 'absolute',
              left: `${tagline.position.x}%`,
              top: `${tagline.position.y}%`,
              transform: 'translate(-50%, -50%)',
              textShadow: darkMode 
                ? '0 0 15px rgba(59, 130, 246, 0.6)' 
                : '0 0 15px rgba(59, 130, 246, 0.4)',
              fontFamily: "'Montserrat', sans-serif",
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              background: darkMode 
                ? 'rgba(30, 58, 138, 0.1)' 
                : 'rgba(219, 234, 254, 0.1)',
            }}
          >
            {/* Pulsing halo/glow effect */}
            <div 
              className={`absolute inset-0 rounded-md -z-10 ${
                darkMode ? 'bg-blue-500' : 'bg-blue-400'
              }`}
              style={{
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                opacity: 0.2,
                filter: 'blur(8px)',
              }}
            />
            {tagline.text}
          </motion.div>
        ))}
      </AnimatePresence>
      
      {/* Add the keyframes for the pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.2;
            transform: scale(1);
          }
          50% {
            opacity: 0.4;
            transform: scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export const HeroSection: React.FC<HeroSectionProps> = ({
  taglines,
  onCtaClick,
  darkMode,
  primaryCtaText = "Get Started",
  secondaryCtaText = "Explore Workflows",
  onSecondaryCtaClick,
  title = "Your collaborative AI platform",
  subtitle = "Your collaborative AI platform for engineering solutions through specialized personas, structured workflows, and artifact generation."
}) => {
  const particlesInit = async (main) => {
    await loadFull(main);
  };

  return (
    <div className={`relative py-16 px-4 overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`} style={{ minHeight: '70vh' }}>
      {/* Animated gradient background */}
      <div className="absolute inset-0 opacity-10">
        <div className={`absolute inset-0 ${darkMode ? 'bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900' : 'bg-gradient-to-br from-blue-100 via-purple-100 to-indigo-100'}`}></div>
        <div className="absolute inset-0">
          <div className={`h-full w-full bg-grid-scifi`} 
               style={{ backgroundSize: '40px 40px' }}>
          </div>
        </div>
      </div>
      
      {/* Particles background */}
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={{
          fullScreen: { enable: false },
          background: {
            color: {
              value: "transparent",
            },
          },
          fpsLimit: 60,
          particles: {
            color: {
              value: darkMode ? "#4F46E5" : "#3B82F6",
            },
            links: {
              color: darkMode ? "#4F46E5" : "#3B82F6",
              distance: 150,
              enable: true,
              opacity: 0.3,
              width: 1,
            },
            collisions: {
              enable: false,
            },
            move: {
              direction: "none",
              enable: true,
              outModes: {
                default: "bounce",
              },
              random: true,
              speed: 1,
              straight: false,
            },
            number: {
              density: {
                enable: true,
                area: 800,
              },
              value: 30,
            },
            opacity: {
              value: 0.3,
            },
            shape: {
              type: "circle",
            },
            size: {
              value: { min: 1, max: 3 },
            },
          },
          detectRetina: true,
        }}
        className="absolute inset-0"
      />
      
      {/* Animated Taglines */}
      {taglines && taglines.length > 0 && (
        <TaglineAnimation 
          taglines={taglines} 
          darkMode={darkMode} 
        />
      )}
      
      <div className="max-w-5xl mx-auto relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
            <span className={`${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{title}</span>
          </h1>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
        >
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </motion.div>
        
        <motion.div 
          className="flex flex-wrap justify-center gap-4 mt-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <button 
              onClick={onCtaClick}
              className={`px-6 py-3 rounded-lg ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-medium flex items-center shadow-lg`}
            >
              {primaryCtaText}
              <FiArrowRight className="ml-2" />
            </button>
          </motion.div>
          
          {onSecondaryCtaClick && (
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <button 
                onClick={onSecondaryCtaClick}
                className={`px-6 py-3 rounded-lg ${darkMode ? 'bg-gray-800 hover:bg-gray-700 border border-blue-500' : 'bg-white hover:bg-gray-100 border border-blue-500'} font-medium flex items-center shadow-lg`}
              >
                {secondaryCtaText}
              </button>
            </motion.div>
          )}
        </motion.div>
        
        {/* Animated scroll indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          animate={{ 
            y: [0, 10, 0],
            opacity: [0.4, 1, 0.4]
          }}
          transition={{ 
            duration: 2, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        >
          <svg className={`w-6 h-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </motion.div>
      </div>
    </div>
  );
};
